import { OrderFormat, ServiceTime } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import ClockIcon from '@ancon/wildcat-ui/shared/icons/clock.svg'
import moment from 'moment'
import { useMemo } from 'react'

import HeadingText from '../../../app/components/HeadingText'
import Button from '../../../app/components/Button'
import BodyText from '../../../app/components/BodyText'
import OutletOrderFormats from '../../../outlets/components/OutletOrderFormats'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { outletOrderConfigFilterModalSelector } from '../../store/outletSelector'
import { OrderConfigureFilterMode } from '../../types'

import styles from './OrderFormatSelectionStep.module.scss'

type OrderFormatSelectionStepProps = {
  orderFormats: OrderFormat[]
  selectedOrderFormat?: OrderFormat
  onChangeOrderFormat: (orderFormat: OrderFormat) => void
  onScheduleOrder: () => void
  selectedServiceTime?: ServiceTime
  onUpdate: () => void
}

export default function OrderFormatSelectionStep({
  orderFormats,
  selectedOrderFormat,
  onChangeOrderFormat,
  onScheduleOrder,
  selectedServiceTime,
  onUpdate,
}: OrderFormatSelectionStepProps) {
  const { t, lang } = useTranslation('common')
  const modalMode = useAppSelector(outletOrderConfigFilterModalSelector)
  const isTableOrder = selectedOrderFormat === OrderFormat.TableOrder
  const isOrderTypeAndTimeFilterModal =
    modalMode === OrderConfigureFilterMode.OrderTypeAndTime
  const isOrderTypeFilterModal =
    modalMode === OrderConfigureFilterMode.OrderType

  function handleOnUpdate() {
    onUpdate()
  }

  const isUpdateButtonDisabled = useMemo(() => {
    switch (modalMode) {
      case OrderConfigureFilterMode.OrderTime:
        return !selectedServiceTime
      case OrderConfigureFilterMode.OrderType:
        return !selectedOrderFormat
      default:
        return !selectedOrderFormat || !selectedServiceTime
    }
  }, [modalMode, selectedOrderFormat, selectedServiceTime])

  return (
    <>
      <div
        className={clsx(styles.modalBody, {
          [styles.modalBodyWidth400]: isOrderTypeFilterModal,
        })}
      >
        <section>
          {isOrderTypeAndTimeFilterModal && (
            <HeadingText as="p" size="h3" className={styles.subTitle}>
              {t('orderConfigurationModal.how')}
            </HeadingText>
          )}
          <OutletOrderFormats
            className={clsx({
              [styles.orderFormatsGrid]:
                modalMode === OrderConfigureFilterMode.OrderType,
            })}
            orderFormats={orderFormats}
            selectedOrderFormat={selectedOrderFormat}
            onChange={onChangeOrderFormat}
          />
        </section>
        {isOrderTypeAndTimeFilterModal && (
          <section>
            <HeadingText as="p" size="h3" className={styles.subTitle}>
              {t('orderConfigurationModal.when')}
            </HeadingText>
            <div
              className={clsx(styles.row, styles.orderTimeSection, {
                [styles.disabled]: isTableOrder,
              })}
            >
              <div className={clsx(styles.row, styles.rowLeft)}>
                <ClockIcon />
                <BodyText as="span" className={styles.capitalize}>
                  {selectedServiceTime?.time
                    ? moment(selectedServiceTime.time)
                        .locale(lang)
                        .calendar({
                          sameDay: `[${t('today')}], LT`,
                          nextDay: `[${t('tomorrow')}] LT`,
                          nextWeek: 'ddd LT',
                          sameElse: 'ddd LT',
                        })
                    : t('orderConfigurationModal.orderNow')}
                </BodyText>
              </div>
              <div className={styles.row}>
                <BodyText>{t('orderConfigurationModal.or')}</BodyText>
                <Button
                  variant="secondary"
                  outlined
                  onClick={onScheduleOrder}
                  disabled={!selectedOrderFormat || isTableOrder}
                >
                  {t('orderConfigurationModal.schedule')}
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
      <div className={styles.modalFooter}>
        <Button disabled={isUpdateButtonDisabled} onClick={handleOnUpdate}>
          {t('orderConfigurationModal.update')}
        </Button>
      </div>
    </>
  )
}
